import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BrainCircuit, CheckCircle2, XCircle, Clock, Loader2, RefreshCw, Eye,
  Users, Sparkles, AlertCircle, Wand2, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminCoachingAPI } from '../lib/api';

const STATUS_TABS = [
  { value: 'by_counselor', label: 'By Counselor' },
  { value: 'pending',      label: 'Pending' },
  { value: 'approved',     label: 'Approved' },
  { value: 'rejected',     label: 'Rejected' },
  { value: 'all',          label: 'All Requests' },
];

const fmtDt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
};

function StatusBadge({ status }) {
  const map = {
    pending:           'bg-amber-100 text-amber-700 border-amber-200',
    approved:          'bg-emerald-100 text-emerald-700 border-emerald-200',
    approved_no_plan:  'bg-orange-100 text-orange-700 border-orange-200',
    rejected:          'bg-rose-100 text-rose-700 border-rose-200',
  };
  return <Badge className={map[status] || 'bg-slate-100 text-slate-700'}>{status}</Badge>;
}

function PlanModal({ preview, onClose }) {
  if (!preview) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[88vh] overflow-hidden flex flex-col shadow-2xl"
           onClick={(e) => e.stopPropagation()} data-testid="plan-preview-modal">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50">
          <div>
            <p className="font-semibold text-slate-900 flex items-center gap-2">
              <BrainCircuit className="h-5 w-5 text-purple-600" />
              Coaching Plan — {preview.counselor_name}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">{preview.ai_model || '—'} · approved {fmtDt(preview.approved_at)}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
        <div className="p-5 overflow-y-auto prose prose-sm max-w-none prose-headings:text-slate-900 prose-h3:text-base prose-h3:font-bold prose-h3:mt-3">
          <ReactMarkdown>{preview.ai_plan}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

export default function CoachingRequests() {
  const [tab, setTab] = useState('by_counselor');
  const [rows, setRows] = useState([]);
  const [byCounselor, setByCounselor] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [preview, setPreview] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      if (tab === 'by_counselor') {
        const r = await adminCoachingAPI.byCounselor();
        setByCounselor(r.data?.counselors || []);
      } else {
        const r = await adminCoachingAPI.list({ status: tab });
        setRows(r.data?.requests || []);
      }
    } catch (_) { toast.error('Failed to load coaching data'); }
    setLoading(false);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tab]);

  const onApprove = async (id) => {
    setActingId(id);
    try {
      const r = await adminCoachingAPI.approve(id);
      if (r.data?.ok) toast.success('Approved — coaching plan generated by Gemini');
      else toast.error(r.data?.error || 'Failed to generate plan');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
    finally { setActingId(null); }
  };
  const onRegenerate = async (counselorId, label) => {
    // coach-regen-v1: bypasses the pending workflow and force-creates a
    // fresh AI plan for the given counselor.
    if (!window.confirm(`Regenerate AI coaching plan for ${label || 'this counselor'}? A new plan will be generated immediately.`)) return;
    setActingId(`regen-${counselorId}`);
    try {
      const r = await adminCoachingAPI.regenerate(counselorId);
      if (r.data?.ok) toast.success('New plan generated by Gemini');
      else toast.error(r.data?.error || 'Plan regeneration failed');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Regenerate failed'); }
    finally { setActingId(null); }
  };
  const onRegenerateMissing = async () => {
    if (!window.confirm('Regenerate plans for every counselor without a fresh plan? This may take a minute.')) return;
    setActingId('regen-bulk');
    try {
      const r = await adminCoachingAPI.regenerateMissing();
      const s = r.data?.summary || {};
      toast.success(`Bulk regenerate complete — ${s.ok || 0} generated, ${s.failed || 0} failed, ${s.skipped || 0} already fresh`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk regenerate failed'); }
    finally { setActingId(null); }
  };
  const onReject = async (id) => {
    if (!window.confirm('Reject this coaching request?')) return;
    setActingId(id);
    try {
      await adminCoachingAPI.reject(id);
      toast.success('Request rejected');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
    finally { setActingId(null); }
  };

  const renderRequestRow = (r) => {
    const sn = r.metrics_snapshot || {};
    return (
      <Card key={r.id} className="border-0 shadow-sm" data-testid={`coaching-row-${r.id}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <span>{r.counselor_name || r.counselor_email || 'Counselor'}</span>
            {r.counselor_email && <Badge variant="outline" className="text-xs">{r.counselor_email}</Badge>}
            <StatusBadge status={r.status} />
            <span className="ml-auto text-xs text-slate-500">{fmtDt(r.requested_at)}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
            {[
              ['Rank',         `#${sn.my_rank ?? '—'} of ${sn.total_in_cohort ?? '—'}`],
              ['Leads (6mo)',  sn.total_leads ?? 0],
              ['Confirmed',    sn.confirmed_total ?? 0],
              ['Conv %',       `${sn.conversion_pct ?? 0}%`],
              ['This Month',   `${sn.month_admissions ?? 0} / ${sn.monthly_target ?? 0}`],
              ['Stale Pending', sn.stale_pending ?? 0],
            ].map(([k, v]) => (
              <div key={k} className="rounded-md bg-slate-50 border border-slate-200 px-2 py-1.5">
                <p className="uppercase text-slate-500">{k}</p>
                <p className="font-semibold">{v}</p>
              </div>
            ))}
          </div>

          {r.status === 'pending' && (
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => onApprove(r.id)} disabled={actingId === r.id}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`approve-${r.id}`}>
                {actingId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                Approve & Generate
              </Button>
              <Button size="sm" variant="outline" onClick={() => onReject(r.id)} disabled={actingId === r.id}
                      className="text-red-600 hover:text-red-700"
                      data-testid={`reject-${r.id}`}>
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
            </div>
          )}
          {(r.status === 'approved' || r.status === 'approved_no_plan') && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span>Approved {fmtDt(r.approved_at)} · {r.ai_model || '—'}</span>
              {r.ai_plan && (
                <Button size="sm" variant="outline" className="ml-auto"
                        onClick={() => setPreview(r)}
                        data-testid={`view-plan-${r.id}`}>
                  <Eye className="h-4 w-4 mr-1" /> View Plan
                </Button>
              )}
              {!r.ai_plan && r.ai_error && (
                <span className="text-red-600 text-xs">Generation failed: {r.ai_error}</span>
              )}
            </div>
          )}
          {r.status === 'rejected' && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="h-4 w-4" /> Rejected {fmtDt(r.rejected_at)}
              <Button size="sm" variant="outline" className="ml-auto"
                      onClick={() => onRegenerate(r.counselor_id, r.counselor_name)}
                      disabled={actingId === `regen-${r.counselor_id}`}
                      data-testid={`regen-${r.counselor_id}`}>
                {actingId === `regen-${r.counselor_id}` ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wand2 className="h-4 w-4 mr-1" />}
                Regenerate
              </Button>
            </div>
          )}
          {(r.status === 'approved' || r.status === 'approved_no_plan') && (
            <div className="flex items-center gap-2 text-xs">
              <Button size="sm" variant="outline"
                      onClick={() => onRegenerate(r.counselor_id, r.counselor_name)}
                      disabled={actingId === `regen-${r.counselor_id}`}
                      data-testid={`regen-${r.counselor_id}`}>
                {actingId === `regen-${r.counselor_id}` ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wand2 className="h-4 w-4 mr-1" />}
                Regenerate Plan
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderByCounselor = () => (
    <div className="space-y-2">
      {byCounselor.length === 0 && !loading && (
        <Card className="border-0 shadow-sm"><CardContent className="py-8 text-sm text-slate-500 text-center">
          No active counselors.
        </CardContent></Card>
      )}
      {byCounselor.map((r) => {
        const c = r.counselor;
        const l = r.latest;
        const status = l?.status || null;
        return (
          <Card key={c.id} className="border-0 shadow-sm" data-testid={`by-counselor-${c.id}`}>
            <CardContent className="py-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 text-white flex items-center justify-center text-sm font-semibold shrink-0">
                  {(c.name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{c.name}</p>
                  <p className="text-xs text-slate-500 truncate">{c.designation || '—'} · {c.email}</p>
                </div>

                {!status && (
                  <>
                    <Badge variant="outline" className="text-xs">No request yet</Badge>
                    <Button size="sm"
                            onClick={() => onRegenerate(c.id, c.name)}
                            disabled={actingId === `regen-${c.id}`}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid={`regen-bc-${c.id}`}>
                      {actingId === `regen-${c.id}` ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                      Generate Plan
                    </Button>
                  </>
                )}
                {status === 'pending' && (
                  <>
                    <StatusBadge status={status} />
                    <span className="text-xs text-slate-500">{fmtDt(l.requested_at)}</span>
                    <Button size="sm" onClick={() => onApprove(l.id)} disabled={actingId === l.id}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`approve-bc-${l.id}`}>
                      {actingId === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                      Approve & Generate
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onReject(l.id)} disabled={actingId === l.id}
                            className="text-red-600">
                      <XCircle className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </>
                )}
                {(status === 'approved' || status === 'approved_no_plan') && (
                  <>
                    <StatusBadge status={status} />
                    <span className="text-xs text-slate-500">{fmtDt(l.approved_at)}</span>
                    {r.is_stale && (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1">
                        <AlertCircle className="h-3 w-3" /> Stale (&gt; 21 d)
                      </Badge>
                    )}
                    {l.ai_plan ? (
                      <Button size="sm" variant="outline" onClick={() => setPreview(l)}
                              data-testid={`view-plan-bc-${l.id}`}>
                        <Eye className="h-4 w-4 mr-1" /> View Plan
                      </Button>
                    ) : (
                      <span className="text-xs text-red-600">No plan generated</span>
                    )}
                    <Button size="sm" variant="outline"
                            onClick={() => onRegenerate(c.id, c.name)}
                            disabled={actingId === `regen-${c.id}`}
                            data-testid={`regen-bc-${c.id}`}>
                      {actingId === `regen-${c.id}` ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wand2 className="h-4 w-4 mr-1" />}
                      Regenerate
                    </Button>
                  </>
                )}
                {status === 'rejected' && (
                  <>
                    <StatusBadge status={status} />
                    <span className="text-xs text-slate-500">{fmtDt(l.rejected_at)}</span>
                    <Button size="sm" variant="outline"
                            onClick={() => onRegenerate(c.id, c.name)}
                            disabled={actingId === `regen-${c.id}`}
                            data-testid={`regen-bc-${c.id}`}>
                      {actingId === `regen-${c.id}` ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wand2 className="h-4 w-4 mr-1" />}
                      Regenerate
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="coaching-requests-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <BrainCircuit className="h-6 w-6 text-purple-600" /> AI Coaching
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Approve plans generated by Gemini 2.5 Flash. Switch to <strong>By Counselor</strong> to see everyone&apos;s latest plan at a glance.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh} data-testid="refresh-coaching">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button size="sm" onClick={onRegenerateMissing}
                    disabled={actingId === 'regen-bulk'}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    data-testid="regen-missing-btn">
              {actingId === 'regen-bulk' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Zap className="h-4 w-4 mr-1" />}
              Regenerate Missing/Stale
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {STATUS_TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} data-testid={`tab-${t.value}`}>
                {t.value === 'by_counselor' ? <Users className="h-4 w-4 mr-1" /> : null}
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {STATUS_TABS.map((t) => (
            <TabsContent key={t.value} value={t.value} className="space-y-3">
              {loading && (
                <div className="py-8 text-sm text-slate-500 flex items-center gap-2 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              )}
              {!loading && t.value === 'by_counselor' && renderByCounselor()}
              {!loading && t.value !== 'by_counselor' && rows.length === 0 && (
                <Card className="border-0 shadow-sm"><CardContent className="py-8 text-sm text-slate-500 text-center">
                  No {t.label.toLowerCase()} requests.
                </CardContent></Card>
              )}
              {!loading && t.value !== 'by_counselor' && rows.map(renderRequestRow)}
            </TabsContent>
          ))}
        </Tabs>

        <PlanModal preview={preview} onClose={() => setPreview(null)} />
      </div>
    </AdminLayout>
  );
}
